
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import MonacoEditor from '@guolao/vue-monaco-editor';

  export default defineComponent({
    name: 'metatags-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
      MonacoEditor,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      const metatags = ref<any>(null);
      const seoSchemaJson = ref('');

      const seoDataEn = ref<any>({
        title: '',
        desc: '',
        keywords: '',
        screen: '',
        og_title: '',
        og_description: '',
        og_url: '',
        og_image: '',
        og_image_alt: '',
        twitter_title: '',
        twitter_description: '',
        twitter_image: '',
        canonical_url: '',
        robots: '',
        schema_data: null,
      });

      const seoDataAr = ref<any>({
        title: '',
        desc: '',
        keywords: '',
        screen: '',
        og_title: '',
        og_description: '',
        og_url: '',
        og_image: '',
        og_image_alt: '',
        twitter_title: '',
        twitter_description: '',
        twitter_image: '',
        canonical_url: '',
        robots: '',
        schema_data: null,
      });

      const loading = ref(true);

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const fetchMetatags = async () => {
        loading.value = true;
        try {
          const { data } = await store.dispatch(
            Actions.GET_MAIN_PAGES_SEO,
            route.params.id
          );
          const payload = data.data ?? data ?? {};
          metatags.value = payload;

          seoDataEn.value = {
            title: payload.titleEn,
            desc: payload.descEn,
            keywords: payload.keywords_en,
            screen: payload.screen,
            og_title: payload.og_title_en,
            og_description: payload.og_description_en,
            og_url: payload.og_url,
            og_image: payload.og_image,
            og_image_alt: payload.og_image_alt_en,
            twitter_title: payload.twitter_title_en,
            twitter_description: payload.twitter_description_en,
            twitter_image: payload.twitter_image,
            canonical_url: payload.canonical_url,
            robots: payload.robots,
            schema_data: payload.schema_data || null,
          };

          seoDataAr.value = {
            title: payload.titleAr,
            desc: payload.descAr,
            keywords: payload.keywords_ar,
            screen: payload.screen_ar,
            og_title: payload.og_title_ar,
            og_description: payload.og_description_ar,
            og_url: payload.og_url,
            og_image: payload.og_image,
            og_image_alt: payload.og_image_alt_ar,
            twitter_title: payload.twitter_title_ar,
            twitter_description: payload.twitter_description_ar,
            twitter_image: payload.twitter_image,
            canonical_url: payload.canonical_url,
            robots: payload.robots,
            schema_data: payload.schema_data || null,
          };

          seoSchemaJson.value = seoDataEn.value.schema_data
            ? JSON.stringify(seoDataEn.value.schema_data, null, 2)
            : '';
        } catch (err) {
          console.error('Error fetching metatags:', err);
          Swal.fire({
            text: translate('errorFetchingData'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-danger',
            },
          });
        } finally {
          loading.value = false;
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );
        }
      };

      const buildCombinedPayload = () => {
        //camel case and snake case mix????
        const payload: any = {
          //en
          titleEn: seoDataEn.value.title,
          descEn: seoDataEn.value.desc,
          keywords_en: seoDataEn.value.keywords,
          screen: seoDataEn.value.screen,
          og_title_en: seoDataEn.value.og_title,
          og_description_en: seoDataEn.value.og_description,
          og_url: seoDataEn.value.og_url,
          og_image: seoDataEn.value.og_image,
          og_image_alt_en: seoDataEn.value.og_image_alt,
          twitter_title_en: seoDataEn.value.twitter_title,
          twitter_description_en: seoDataEn.value.twitter_description,

          //ar
          titleAr: seoDataAr.value.title,
          descAr: seoDataAr.value.desc,
          keywords_ar: seoDataAr.value.keywords,
          og_title_ar: seoDataAr.value.og_title,
          og_description_ar: seoDataAr.value.og_description,
          og_image_alt_ar: seoDataAr.value.og_image_alt,
          twitter_title_ar: seoDataAr.value.twitter_title,
          twitter_description_ar: seoDataAr.value.twitter_description,

          //common
          twitter_image: seoDataEn.value.twitter_image,
          canonical_url: seoDataEn.value.canonical_url,
          robots: seoDataEn.value.robots,
          schema_data: seoSchemaJson.value
            ? JSON.parse(seoSchemaJson.value)
            : null,
        };

        return payload;
      };

      const onSubmitBoth = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const payload = buildCombinedPayload();

        try {
          await store.dispatch(Actions.UPDATE_MAIN_PAGES_SEO, {
            id: route.params.id,
            data: payload,
          });

          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_SEO_CONFIGURATION'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'metatags-listing' });
          });
        } catch (err) {
          console.error('Update error:', err);
          const errorMsg =
            store.getters.getErrors?.message ||
            'An error occurred while updating SEO configuration';
          Swal.fire({
            text: translate(errorMsg),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } finally {
          submitButton.value?.removeAttribute('data-kt-indicator');
          if (submitButton.value) submitButton.value.disabled = false;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('seoConfiguration'), [
          translate('seo'),
        ]);
        fetchMetatags();
      });

      await store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        submitButton,
        translate,
        goBack,
        seoDataEn,
        seoDataAr,
        can,
        fetchMetatags,
        onSubmitBoth,
        loading,
        seoSchemaJson,
      };
    },
  });
